'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { logsService } from '@/lib/api/logsService';
import { LogEntry } from '@/types/log';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import ErrorMessage from '@/components/common/ErrorMessage';

const criterios = [
  { value: 'accion', label: 'Acción' },
  { value: 'solicitud_numero_control', label: 'Número de Control' },
  { value: 'cliente_nombre', label: 'Nombre del Cliente' },
  { value: 'user_name', label: 'Usuario' },
  { value: 'field', label: 'Campo' },
];

const LogsManagement: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [criterio, setCriterio] = useState('accion');
  const [valor, setValor] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  const load = async () => {
    setIsLoading(true);
    setError(null);
    const res = await logsService.getAll({
      fechaInicio: fechaInicio || undefined,
      fechaFin: fechaFin || undefined,
      filtroCriterio: criterio || undefined,
      filtroValor: valor || undefined,
    });
    if (res.data) setLogs(res.data);
    else setError(res.error || 'Error al cargar logs');
    setIsLoading(false);
  };

  useEffect(() => {
    // Establecer fecha por defecto y cargar una sola vez
    const now = new Date();
    const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const defaultInicio = firstDayLastMonth.toISOString().split('T')[0];
    const defaultFin = now.toISOString().split('T')[0];
    setFechaInicio(defaultInicio);
    setFechaFin(defaultFin);

    // Carga inicial con las fechas por defecto
    (async () => {
      setIsLoading(true);
      setError(null);
      const res = await logsService.getAll({
        fechaInicio: defaultInicio,
        fechaFin: defaultFin,
        filtroCriterio: criterio || undefined,
        filtroValor: valor || undefined,
      });
      if (res.data) setLogs(res.data);
      else setError(res.error || 'Error al cargar logs');
      setIsLoading(false);
    })();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Logs</h1>
          <p className="text-sm text-gray-500 mt-1">Registro de cambios sobre solicitudes.</p>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <Input 
            label="Desde" 
            type="date" 
            value={fechaInicio} 
            onChange={(e) => setFechaInicio(e.target.value)}
            min="2019-01-02"
          />
          <Input 
            label="Hasta" 
            type="date" 
            value={fechaFin} 
            onChange={(e) => setFechaFin(e.target.value)}
            min="2019-01-02"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Criterio</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={criterio}
              onChange={(e) => setCriterio(e.target.value)}
            >
              {criterios.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <Input label="Valor" value={valor} onChange={(e) => setValor(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setValor(''); load(); }}>Limpiar</Button>
            <Button onClick={load}>Consultar</Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full w-full text-sm">
            <thead className="bg-gray-50 text-gray-700 sticky top-0">
              <tr>
                <th className="text-left p-3 border-r border-gray-200">#</th>
                <th className="text-left p-3 border-r border-gray-200">Objeto</th>
                <th className="text-left p-3 border-r border-gray-200">Acción</th>
                <th className="text-left p-3 border-r border-gray-200">Momento</th>
                <th className="text-left p-3 border-r border-gray-200">Usuario</th>
                <th className="text-left p-3">Campo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((l, idx) => {
                // Formatear timestamp
                const formatTimestamp = (ts: string) => {
                  try {
                    const date = new Date(ts);
                    return date.toLocaleString('es-MX', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    });
                  } catch {
                    return ts;
                  }
                };
                
                // Formatear campo (reemplazar comas por saltos de línea)
                const formatField = (field: string | undefined) => {
                  if (!field) return '—';
                  return field.split(',').map(f => f.trim()).join('\n');
                };

                return (
                  <tr key={l.id ?? idx} className="hover:bg-blue-50/40 transition-colors">
                    <td className="p-3 border-r border-gray-200 font-medium">{l.id}</td>
                    <td className="p-3 border-r border-gray-200">{l.objeto}</td>
                    <td className="p-3 border-r border-gray-200">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        l.accion === 'agregar' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {l.accion}
                      </span>
                    </td>
                    <td className="p-3 border-r border-gray-200 text-xs">{formatTimestamp(l.time_stamp)}</td>
                    <td className="p-3 border-r border-gray-200">{l.user_name || '—'}</td>
                    <td className="p-3">
                      {l.accion === 'agregar' ? (
                        <span className="text-gray-400 italic">—</span>
                      ) : (
                        <div className="text-xs whitespace-pre-line break-words max-w-xs">
                          {formatField(l.field)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {logs.length === 0 && (
                <tr>
                  <td className="p-6 text-gray-500 text-center" colSpan={6}>
                    Sin registros con los filtros actuales.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LogsManagement;
